import { jobMatchesDay } from './jobSchedule';

/**
 * Numeric sell price on a job, or null if missing/invalid.
 */
export function parseJobPriceAmount(job) {
  if (!job) return null;
  const raw = job.price;
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function formatDriverMoney(amount, currency = 'GBP') {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '—';
  const cur = String(currency || 'GBP').trim() || 'GBP';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(n);
  } catch {
    return `${n.toFixed(2)} ${cur}`.trim();
  }
}

function isCancelled(job) {
  return String(job?.status || '').toLowerCase() === 'cancelled';
}

function isCompleted(job) {
  return String(job?.status || '').toLowerCase() === 'completed';
}

/**
 * Turnover summary for one calendar day (driver portal).
 *
 * @param {object[]} jobs
 * @param {import('dayjs').Dayjs} day
 */
export function computeDayTurnover(jobs, day) {
  const dayJobs = (jobs || []).filter((j) => jobMatchesDay(j, day));
  const active = dayJobs.filter((j) => !isCancelled(j));

  /** @type {Record<string, { total: number, completed: number, pricedJobs: number }>} */
  const byCurrency = {};

  let jobsWithoutPrice = 0;

  for (const job of active) {
    const amount = parseJobPriceAmount(job);
    if (amount === null) {
      jobsWithoutPrice += 1;
      continue;
    }
    const currency = String(job.currency || 'GBP').trim() || 'GBP';
    if (!byCurrency[currency]) {
      byCurrency[currency] = { total: 0, completed: 0, pricedJobs: 0 };
    }
    byCurrency[currency].total += amount;
    byCurrency[currency].pricedJobs += 1;
    if (isCompleted(job)) {
      byCurrency[currency].completed += amount;
    }
  }

  const currencies = Object.entries(byCurrency)
    .map(([currency, row]) => ({
      currency,
      total: row.total,
      completed: row.completed,
      remaining: Math.max(0, row.total - row.completed),
      pricedJobs: row.pricedJobs,
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));

  return {
    jobCount: active.length,
    completedJobCount: active.filter(isCompleted).length,
    cancelledJobCount: dayJobs.length - active.length,
    jobsWithoutPrice,
    currencies,
    hasPricedJobs: currencies.length > 0,
  };
}
